import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  MdThumbUp,
  MdThumbDown,
  MdSubscriptions,
  MdOutlineComment,
  MdPlaylistAdd,
  MdOutlineAlbum,
  MdClose,
} from 'react-icons/md';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import './SignInPromptModal.css';

export default function SignInPromptModal({
  isOpen,
  action = 'general',
  targetName = '',
  isAlbum = false,
  isShort = false,
  onSignIn,
  onClose,
}) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isExiting, setIsExiting] = useState(false);
  const closingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsExiting(false);
      closingRef.current = false;
    } else if (shouldRender && !closingRef.current) {
      closingRef.current = true;
      setIsExiting(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
        closingRef.current = false;
      }, 220);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  const initiateClose = (actionCallback) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setIsExiting(true);
    setTimeout(() => {
      if (actionCallback) actionCallback();
      if (onClose) onClose();
      setShouldRender(false);
      setIsExiting(false);
      closingRef.current = false;
    }, 220);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      initiateClose(null);
    }
  };

  const handleSignInClick = () => {
    initiateClose(onSignIn);
  };

  if (!shouldRender && !isOpen) return null;

  // Compute dynamic text & icon based on action and context
  let icon = <HiOutlineUserCircle size={28} />;
  let title = 'Sign in to SpeedoTube';
  let message = 'Sign in to like videos, comment, and subscribe.';

  const itemType = isAlbum ? 'album' : isShort ? 'short' : 'video';

  switch (action) {
    case 'like':
      icon = <MdThumbUp size={24} />;
      title = isAlbum ? 'Like this album?' : isShort ? 'Like this short?' : 'Like this video?';
      message = 'Sign in to make your opinion count.';
      break;

    case 'dislike':
      icon = <MdThumbDown size={24} />;
      title = isAlbum ? "Don't like this album?" : isShort ? "Don't like this short?" : "Don't like this video?";
      message = 'Sign in to make your opinion count.';
      break;

    case 'subscribe':
      icon = <MdSubscriptions size={24} />;
      title = 'Want to subscribe to this channel?';
      message = targetName
        ? `Sign in to subscribe to ${targetName}.`
        : 'Sign in to subscribe to this channel.';
      break;

    case 'comment':
      icon = <MdOutlineComment size={24} />;
      title = 'Want to join the conversation?';
      message = 'Sign in to add a comment, reply, and interact with the community.';
      break;

    case 'save':
      icon = <MdPlaylistAdd size={26} />;
      title = isAlbum ? 'Want to save this album?' : 'Want to watch this again later?';
      message = isAlbum
        ? 'Sign in to save this album to your library.'
        : 'Sign in to add this video to a playlist.';
      break;

    case 'album':
      icon = <MdOutlineAlbum size={26} />;
      title = 'Want to interact with this album?';
      message = 'Sign in to like songs, leave comments, and save albums to your library.';
      break;

    default:
      icon = <HiOutlineUserCircle size={28} />;
      title = 'Sign in to SpeedoTube';
      message = 'Sign in to enjoy all features including likes, comments, and subscriptions.';
      break;
  }

  return ReactDOM.createPortal(
    <div
      className={`yt-signin-modal-backdrop ${isExiting ? 'exiting' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="yt-signin-modal-title"
    >
      <div className={`yt-signin-modal-card ${isExiting ? 'exiting' : ''}`}>
        <button
          type="button"
          className="yt-signin-modal-close-btn"
          onClick={() => initiateClose(null)}
          aria-label="Close"
        >
          <MdClose size={20} />
        </button>

        <div className="yt-signin-modal-header">
          <div className="yt-signin-modal-icon-wrap">{icon}</div>
          <h2 id="yt-signin-modal-title" className="yt-signin-modal-title">
            {title}
          </h2>
        </div>

        <div className="yt-signin-modal-body">
          <p>{message}</p>
        </div>

        <div className="yt-signin-modal-actions">
          <button
            type="button"
            className="yt-signin-btn-cancel"
            onClick={() => initiateClose(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="yt-signin-btn-primary"
            onClick={handleSignInClick}
            autoFocus
          >
            Sign in
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
